import { useCallback, useMemo } from "react";
import { useEngagementSummary } from "@/lib/api/mentorships";
import {
  dayCategoriesFromSummary,
  dayRundownFromCounts,
  HISTORY_DAY_TIMEZONE_FALLBACK,
  monthSummaryFromTotals,
  type DayCategories,
  type DayRundown,
  type MonthSummary,
} from "./history-model";

interface UseHistoryCalendarParams {
  mentorshipId: string | null;
  visibleMonth: Date;
}

export interface UseHistoryCalendarResult {
  dayCategories: Record<string, DayCategories>;
  monthSummary: MonthSummary;
  getDayRundown: (dayKey: string) => DayRundown;
  timezone: string;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
}

const EMPTY_SUMMARY = monthSummaryFromTotals({
  activities: 0,
  content: 0,
  messages: 0,
  videoCalls: 0,
  daysEngaged: 0,
  mostUsedActivityType: null,
});

export function useHistoryCalendar({
  mentorshipId,
  visibleMonth,
}: UseHistoryCalendarParams): UseHistoryCalendarResult {
  const year = visibleMonth.getFullYear();
  const month = visibleMonth.getMonth() + 1;

  const { data, isLoading, isError, refetch } = useEngagementSummary(
    mentorshipId ?? "",
    month,
    year,
  );

  const dayCategories = useMemo(
    () => dayCategoriesFromSummary(data?.days ?? {}),
    [data],
  );

  const monthSummary = useMemo(
    () => (data ? monthSummaryFromTotals(data.totals) : EMPTY_SUMMARY),
    [data],
  );

  const getDayRundown = useCallback(
    (dayKey: string) => dayRundownFromCounts(data?.days[dayKey]),
    [data],
  );

  return {
    dayCategories,
    monthSummary,
    getDayRundown,
    timezone: data?.timezone ?? HISTORY_DAY_TIMEZONE_FALLBACK,
    isLoading: mentorshipId === null || isLoading,
    isError,
    refetch: () => void refetch(),
  };
}
